import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Upload, FolderKanban, FileText, Paperclip, Trash2, LoaderCircle, Lock } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import DocumentSelectorModal from '../../doc-control/components/DocumentSelectorModal'

const DASH = '—'

const formatDate = (t: (key: string, opts?: any) => string, value?: string | null) => {
  if (!value) return DASH
  return new Date(value).toLocaleDateString(t('date_locale') || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

interface AttachmentRowProps {
  attachment: any
  canRemove: boolean
  removing: boolean
  onRemove: () => void
}

const AttachmentRow: React.FC<AttachmentRowProps> = ({ attachment, canRemove, removing, onRemove }) => {
  const { t } = useTranslation()
  const isDocLink = !!attachment.docRevisionId
  const filename = isDocLink ? attachment.docRevision?.file?.filename : attachment.file?.filename
  const href = isDocLink ? attachment.docRevision?.file?.url : attachment.file?.url
  const Icon = isDocLink ? FileText : Paperclip

  return (
    <div className="flex items-center gap-2 rounded border px-2.5 py-2 text-sm hover:bg-muted/40">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 truncate text-primary hover:underline"
          title={filename}
        >
          {filename || DASH}
        </a>
      ) : (
        <span className="flex-1 truncate" title={filename}>
          {filename || DASH}
        </span>
      )}
      {isDocLink && (
        <Badge variant="secondary" className="whitespace-nowrap">
          {t('Submittals_attachment_doc_control_badge')}
        </Badge>
      )}
      <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
        {attachment.addedBy?.name} · {formatDate(t, attachment.createdAt)}
      </span>
      {canRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={removing}
          title={t('Common_delete')}
        >
          {removing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
      )}
    </div>
  )
}

interface SubmittalAttachmentsCardProps {
  submittal: any
  /** The submittal's CURRENT revision (submittal.currentRevision) — used as the
   *  default selection and as the yardstick for "am I viewing the current one". */
  revision: any
  /** Coarse editability from the parent: status===DRAFT && user may edit. Combined
   *  here with "is the currently-selected revision the current one" to get the
   *  final per-action gate, since older revisions are always read-only. */
  editable: boolean
  refetch: () => void
  /** Revision switcher state, owned by index.tsx (not this card) so that
   *  SubmittalReviewsCard can follow the exact same selection — see the
   *  brief for Task 16 step 3 ("reviews shown must follow the same selected
   *  revision"). */
  selectedRevisionId: string
  onSelectRevisionChange: (id: string) => void
}

/**
 * Lists attachments for a submittal revision, with a Select to browse older
 * revisions (read-only) and, on the current revision while editable, two ways
 * to add: a plain file upload and a picker into Doc Control.
 *
 * Upload note: CreateFileInput has no submittal-specific relation (only
 * snagId/meetingId/sessionId/tenderId) — see MeetingFileUpload for the
 * meeting-coupled equivalent, which cannot be reused as-is. This uses the
 * same generic `directory`-based path FileManager (components/file-manager)
 * already relies on for entity-less uploads, with the dedicated 'submittals'
 * directory (src/graphql/file.ts's allowlist) — kept out of 'general' (the
 * organization Documents tab's own directory) so these uploads don't leak
 * into that org-wide, deletable-by-anyone-with-access listing.
 */
const SubmittalAttachmentsCard: React.FC<SubmittalAttachmentsCardProps> = ({
  submittal,
  revision,
  editable,
  refetch,
  selectedRevisionId,
  onSelectRevisionChange,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [docSelectorOpen, setDocSelectorOpen] = useState(false)
  const [addingDocs, setAddingDocs] = useState(false)

  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE)
  const [addAttachment] = useMutation(Mutation.ADD_SUBMITTAL_ATTACHMENT)
  const [removeAttachment] = useMutation(Mutation.REMOVE_SUBMITTAL_ATTACHMENT)
  const [upload] = useUpload()

  const revisions: any[] = submittal.revisions || []
  const displayedRevision = revisions.find((r) => r.id === selectedRevisionId) || revision
  const isViewingCurrent = displayedRevision.id === revision.id
  const canMutate = editable && isViewingCurrent
  const attachments: any[] = displayedRevision.attachments || []

  // Documents already linked on the CURRENT revision shouldn't be offered again.
  // Uses `attachments` (already scoped to displayedRevision), not `revision.attachments`
  // — `revision` here is submittal.currentRevision, and SubmittalRowFragment only
  // selects `{ id index }` on it (no .attachments), so that would always be
  // undefined and silently exclude nothing. `attachments` is safe to use: it's
  // only read below by the "From Doc Control" button, which renders solely when
  // canMutate is true — and canMutate requires isViewingCurrent, i.e.
  // displayedRevision === revision, so the two lists are identical at that point.
  const excludeDocumentIds: string[] = attachments
    .map((a: any) => a.docRevision?.documentId)
    .filter(Boolean)
    .filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i)

  const handleUploadClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return

    setUploading(true)
    try {
      const prepared = await Promise.all(
        files.map(async (file) => {
          const { data } = await createFile({
            variables: {
              input: {
                filename: file.name,
                size: file.size,
                mimetype: file.type || 'application/octet-stream',
                encoding: 'utf8',
                directory: 'submittals',
              },
            },
          })
          return { file, id: data.createFile.file.id, name: file.name, signedUrl: data.createFile.uploadUrl }
        }),
      )

      const result = await upload(prepared)
      // useUpload resolves to { cancelled, fileIds } instead of an id array when
      // the transfer was aborted — drop the orphaned File rows it already created.
      if (!Array.isArray(result)) {
        const orphans = (result as any)?.fileIds || []
        if (orphans.length) await deleteFile({ variables: { ids: orphans } })
        return
      }

      await setFileUploaded({ variables: { ids: result } })
      for (const fileId of result) {
        await addAttachment({ variables: { revisionId: revision.id, fileId } })
      }
      toastr.success('', t('Submittals_attachment_added'))
      refetch()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async (attachmentId: string) => {
    setRemovingId(attachmentId)
    try {
      await removeAttachment({ variables: { id: attachmentId } })
      toastr.success('', t('Submittals_attachment_removed'))
      refetch()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setRemovingId(null)
    }
  }

  const handleDocSelect = async (documents: any[]) => {
    // The modal already closes itself (calls onSelect then its own onClose)
    // before this resolves, so the guard belongs on the trigger button below,
    // not on docSelectorOpen.
    setAddingDocs(true)
    try {
      for (const doc of documents) {
        const docRevisionId = doc.currentRevisionId ?? doc.currentRevision?.id
        if (!docRevisionId) continue
        await addAttachment({ variables: { revisionId: revision.id, docRevisionId } })
      }
      toastr.success('', t('Submittals_attachment_added'))
      refetch()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setAddingDocs(false)
    }
  }

  return (
    <Card className="shadow-sm border-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="text-base">{t('Submittals_attachments_title')}</CardTitle>
        <div className="flex items-center gap-2">
          {revisions.length > 1 && (
            <Select value={selectedRevisionId} onValueChange={onSelectRevisionChange}>
              <SelectTrigger className="h-8 w-auto min-w-[110px] gap-2 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {revisions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {t('Submittals_rev_chip', { index: r.index })}
                    {r.id === revision.id ? ` ${t('Submittals_current_revision_suffix')}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canMutate && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {t('Submittals_attachment_upload_btn')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setDocSelectorOpen(true)}
                disabled={addingDocs}
              >
                {addingDocs ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FolderKanban className="h-4 w-4" />}
                {t('Submittals_attachment_from_doc_control_btn')}
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!isViewingCurrent && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 text-sm text-amber-900 mb-3">
            <Lock className="h-4 w-4 text-amber-600 shrink-0" />
            {t('Submittals_old_revision_hint')}
          </div>
        )}

        {attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6 mb-0">
            {t('Submittals_attachments_empty')}
          </p>
        ) : (
          <div className="space-y-1.5">
            {attachments.map((a) => (
              <AttachmentRow
                key={a.id}
                attachment={a}
                canRemove={canMutate}
                removing={removingId === a.id}
                onRemove={() => handleRemove(a.id)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {docSelectorOpen && (
        <DocumentSelectorModal
          isOpen={docSelectorOpen}
          onClose={() => setDocSelectorOpen(false)}
          onSelect={handleDocSelect}
          excludeDocumentIds={excludeDocumentIds}
          projectId={submittal.projectId}
        />
      )}
    </Card>
  )
}

export default SubmittalAttachmentsCard
